import React from 'react';

import { Box, Display, Flex, Heading, Image } from '@rover/kibble/core';

import couponHero from './images/coupon-hero.png';
import CouponTicket from './images/yellow-coupon.svg';

type CouponIllustrationProps = {
  variant: 'hero' | 'compact';
  symbol: string;
  amount: number | string;
};

const HERO_SIZE = { width: '200px', height: '201px' };
const COMPACT_SIZE = { width: '51.36px', height: '30.21px' };
const HERO_FONT = { number: '30px', symbol: '18px' };
const COMPACT_FONT = { number: '9px', symbol: '6px' };

const CouponIllustration = ({ variant, symbol, amount }: CouponIllustrationProps): JSX.Element => {
  const label = `Rover coupon for ${symbol}${amount} off your first booking`;

  const isHero = variant === 'hero';
  const font = isHero ? HERO_FONT : COMPACT_FONT;

  const amountOverlay = (
    <Flex aria-hidden flexDirection="row" alignItems="baseline" justifyContent="center">
      {symbol ? (
        <Heading
          size="100"
          mb="0x"
          textColor="primary"
          sx={{ fontSize: font.symbol, lineHeight: 1 }}
          webProps={{ as: 'span' }}
        >
          {symbol}
        </Heading>
      ) : null}
      <Display
        size="400"
        mb="0x"
        textColor="primary"
        sx={{ fontSize: font.number, lineHeight: 1 }}
        webProps={{ as: 'span' }}
      >
        {amount}
      </Display>
    </Flex>
  );

  if (variant === 'compact') {
    // Box uses the coupon badge's exact Figma dimensions (51.36 × 30.21).
    // `preserveAspectRatio="…meet"` fits the vector without stretching, and the
    // parent row's `alignItems="center"` keeps it vertically centred against the
    // copy.
    return (
      <Box
        position="relative"
        flexShrink="0"
        width={COMPACT_SIZE.width}
        height={COMPACT_SIZE.height}
        webProps={{ role: 'img', 'aria-label': label }}
      >
        <Box aria-hidden width="100%" height="100%">
          <CouponTicket width="100%" height="100%" preserveAspectRatio="xMidYMid meet" />
        </Box>
        <Flex
          position="absolute"
          top="0x"
          bottom="0x"
          left="0x"
          right="0x"
          alignItems="center"
          justifyContent="center"
        >
          {amountOverlay}
        </Flex>
      </Box>
    );
  }

  // Box matches the composited PNG's 192:193 aspect. The amount sits over the
  // ticket's blank face, which is horizontally centred and ~72% down the image
  // (the pets occupy the top).
  return (
    <Box position="relative" width={HERO_SIZE.width} height={HERO_SIZE.height} flexShrink="0">
      <Image
        src={couponHero}
        width="100%"
        height="100%"
        resizeMethod="contain"
        description={label}
      />
      <Flex
        position="absolute"
        top="72%"
        left="0x"
        right="0x"
        justifyContent="center"
        sx={{ transform: 'translateY(-50%)' }}
      >
        {amountOverlay}
      </Flex>
    </Box>
  );
};

export default CouponIllustration;
